import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Tooltip,
  IconButton,
  Alert,
  Paper,
  Chip,
} from '@mui/material';
import { AgGridReact, CustomCellRendererProps } from 'ag-grid-react';
import {
  Add,
  Edit,
  Delete,
  Dashboard,
  ContentCopy,
  DisabledByDefault,
  Download,
  UploadFile,
  Security,
} from '@mui/icons-material';
import CampaignDialog from '../CampaignDialog';
import useCampaign from '../useCampaign';
import ConfirmModal from '../../../components/ConfirmModal';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { showMessage } from '../../../components/utils/showMessage';
import UploadDialog from '../UploadDialog';
import { ColDef } from 'ag-grid-community';
import { CampaignContext } from '../context';
import IronDomeImporter from '../../../components/IronDomeImporter';

function CampaignTable() {
  const {
    getCampaigns,
    getCampaignsWithOffers,
    addCampaign,
    editCampaign,
    deleteCampaign,
    bulkAddCampaignOffers,
    ironDomeBulkImport,
  } = useCampaign();
  const navigate = useNavigate();

  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [selectedCampaign, setSelectedCampaign] = useState<any>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [uploadErrors, setUploadErrors] = useState<string | null>(null);

  const { offerGroups } = useContext(CampaignContext);

  const offerGroupMap = Object.fromEntries(
    offerGroups.map((item) => [item._id, item])
  );

  const handleDialogOpen = (campaign: any = null) => {
    setSelectedCampaign(campaign);
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setSelectedCampaign(null);
  };

  const handleSaveCampaign = async (campaignData: any) => {
    try {
      setLoading(true);
      if (selectedCampaign) {
        await editCampaign().call(selectedCampaign._id, campaignData);
        showMessage({
          message: 'Campaign updated successfully!',
          severity: 'success',
        });
      } else {
        await addCampaign().call(campaignData);
        showMessage({
          message: 'Campaign added successfully!',
          severity: 'success',
        });
      }
      handleDialogClose();
      fetchData();
    } catch (error) {
      showMessage({ message: 'Error saving campaign.', severity: 'error' });
      console.error('Error saving campaign:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCampaign = async (id: string) => {
    try {
      setLoading(true);
      await deleteCampaign().call(id);
      showMessage({
        message: 'Campaign deleted successfully!',
        severity: 'success',
      });
      fetchData();
    } catch (error) {
      showMessage({ message: 'Error deleting campaign.', severity: 'error' });
      console.error('Error deleting campaign:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const campaigns = await getCampaigns().call();
      setData(campaigns);
      setTotalRowCount(campaigns.length);
    } catch (error) {
      showMessage({ message: 'Error fetching campaigns.', severity: 'error' });
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDownloadTemplate = async () => {
    const headers = [
      'campaignName',
      'entryDomain',
      'offerGroupName',
      'merchantId',
      'merchantName',
      'merchantUrl',
      'affiliateUrl',
      'clickCap',
      'commission',
      'geo',
      'device',
      'status',
    ];
    const csvRows = [];
    csvRows.push(headers.join(','));
    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('hidden', '');
    a.setAttribute('href', url);
    a.setAttribute('download', 'campaign_offer_template.csv');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleUploadDialogOpen = () => {
    setUploadErrors(null);
    setIsUploadDialogOpen(true);
  };

  const handleUploadDialogClose = () => {
    setUploadErrors(null);
    setIsUploadDialogOpen(false);
  };


  const handleFileSubmit = (
    file: File,
    setLocalButtonLoading: (loading: boolean) => void,
    createIfNotExist: boolean
  ) => {
    setUploadErrors(null);
    const reader = new FileReader();
    reader.onload = async (e: ProgressEvent<FileReader>) => {
      const text = e.target?.result as string;
      const rows = text.split('\n').filter((row) => row.trim() !== '');
      if (rows.length < 2) {
        setUploadErrors('Uploaded file is empty or invalid.');
        setLoading(false);
        setLocalButtonLoading(false);
        return;
      }
      const headers = rows[0].split(',').map((h) => h.trim());
      const offersArray = rows.slice(1).map((row) => {
        const values = row.split(',').map((v) => v.trim());
        const offer: { [key: string]: string } = {};
        headers.forEach((header, index) => {
          offer[header] = values[index];
        });
        return offer;
      });
      try {
        setLoading(true);
        await bulkAddCampaignOffers().call(offersArray, createIfNotExist);
        showMessage({ message: 'Bulk upload successful', severity: 'success' });
        setUploadErrors(null);
        handleUploadDialogClose();
        fetchData();
      } catch (err: any) {
        setUploadErrors(
          err?.response?.data?.message || err.message || 'Bulk upload failed'
        );
        console.error(err);
      } finally {
        setLoading(false);
        setLocalButtonLoading(false);
      }
    };
    reader.readAsText(file);
  };


  const onPaginationChanged = (event: any) => {
    const currentPage = event.api.paginationGetCurrentPage();
    const pageSize = event.api.paginationGetPageSize();

    if (paginationModel.pageSize !== pageSize) {
      setPaginationModel({ page: currentPage, pageSize });
      fetchData();
    } else if (paginationModel.page !== currentPage) {
      setPaginationModel({ page: currentPage, pageSize });
      fetchData();
    }
  };

  const columns: ColDef[] = [
    {
      headerName: 'Name',
      field: 'name',
      flex: 1,
      cellRenderer: (params: any) => (
        <Link
          to={`/campaigns/${params.data._id}/offers`}
          style={{ textDecoration: 'none', color: 'inherit' }}
        >
          {params.value}
        </Link>
      ),
    },
    { headerName: 'Entry Domain', field: 'entryDomain', flex: 1 },
    {
      headerName: 'Offers',
      field: 'offers',
      flex: 1,
      filter: false,
      sortable: false,
      cellRenderer: (params: any) => (
        <Tooltip title="View Offers">
          <Link
            to={`/campaigns/${params.data._id}/offers`}
            style={{ color: 'inherit' }}
          >
            <IconButton>
              <Dashboard />
            </IconButton>
          </Link>
        </Tooltip>
      ),
    },
    {
      headerName: 'Reporting',
      field: 'reportOfferGroupId',
      flex: 1,
      filter: false,
      sortable: false,
      cellRenderer: (params: CustomCellRendererProps) => {
        const { value } = params;

        const label = offerGroupMap[value]?.label || '';

        if (!label) return <Chip label="No offer group" color="error" />;

        return (
          <Link to={`/offer-group/${value}`}>
            <Chip label={label} />
          </Link>
        );
      },
    },
    {
      headerName: 'Actions',
      field: 'actions',
      flex: 1,
      filter: false,
      sortable: false,
      cellRenderer: (params: any) => (
        <Box display="flex" gap={1}>
          <Tooltip title="Edit Campaign">
            <IconButton
              size="small"
              onClick={() => handleDialogOpen(params.data)}
            >
              <Edit />
            </IconButton>
          </Tooltip>
          <Tooltip title="Copy Entry Domain">
            <IconButton
              size="small"
              onClick={() => {
                navigator.clipboard.writeText(params.data.entryDomain);
                showMessage({
                  message: 'Entry domain copied to clipboard!',
                  severity: 'success',
                });
              }}
            >
              <ContentCopy />
            </IconButton>
          </Tooltip>
          <a
            href={`campaigns/${params.data._id}/block`}
            style={{ display: 'inline-flex', color: 'inherit' }}
            title="Block rules"
            onClick={(e) => {
              // Only handle left click (not ctrl/cmd/shift/alt or middle/right click)
              if (
                !e.ctrlKey &&
                !e.metaKey &&
                !e.shiftKey &&
                !e.altKey &&
                e.button === 0
              ) {
                e.preventDefault();
                navigate(`${params.data._id}/block`);
              }
            }}
          >
            <IconButton size="small">
              <DisabledByDefault />
            </IconButton>
          </a>
          <ConfirmModal
            iconButton
            icon={<Delete />}
            title="Delete Campaign"
            onConfirm={() => handleDeleteCampaign(params.data._id)}
          >
            <Alert severity="warning">
              Are you sure you want to delete this campaign? This action cannot
              be undone.
            </Alert>
          </ConfirmModal>
        </Box>
      ),
    },
  ];

  const handleExportOffers = async () => {
    try {
      setLoading(true);
      const campaigns = await getCampaignsWithOffers().call();

      const headers = [
        'campaignName',
        'entryDomain',
        'merchantId',
        'merchantName',
        'merchantUrl',
        'affiliateUrl',
        'clickCap',
        'commission',
        'geo',
        'device',
        'status',
      ];

      const rows = [headers.join(',')];

      campaigns.forEach((campaign: any) => {
        (campaign.offers || []).forEach((offer: any) => {
          const row = [
            campaign.name || '',
            campaign.entryDomain || '',
            offer.merchantId || '',
            offer.merchantName || '',
            offer.merchantUrl || '',
            offer.affiliateUrl || '',
            offer.clickCap || '',
            offer.commission || '',
            offer.geo || '',
            offer.device || '',
            offer.status || '',
          ];
          rows.push(row.map((cell) => `"${cell}"`).join(','));
        });
      });

      if (rows.length === 1) {
        showMessage({ message: 'No offers to export.', severity: 'info' });
        return;
      }

      const csvString = rows.join('\n');
      const blob = new Blob([csvString], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.setAttribute('href', url);
      a.setAttribute('download', 'campaign_offers_export.csv');
      a.click();
      a.remove();
    } catch (error) {
      console.error('Error exporting offers:', error);
      showMessage({ message: 'Error exporting offers.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleIronDomeExport = async () => {
    try {
      setLoading(true);
      const campaigns = await getCampaigns().call();

      const headers = [
        'campaignName',
        'blockAnon',
        'blockDataCenter',
        'blockIpGeneral',
        'blockIpDdos',
        'blockIpRecon',
        'blockLocation',
        'ipThrottle',
        'fallbackLink',
        'isPassback',
        'passbackUrl',
      ];

      const rows = [headers.join(',')];

      campaigns.forEach((campaign: any) => {
        const blockRules = campaign.blockRules || [];

        if (blockRules.length === 0) {
          const row = [
            campaign.name || '',
            'false',
            'false',
            'false',
            'false',
            'false',
            '',
            '',
            '',
            'false',
            '',
          ];
          rows.push(row.map((cell) => `"${cell}"`).join(','));
        } else {
          blockRules.forEach((rule: any) => {
            let blockLocation = '';
            if (rule.blockLocations && rule.blockLocations.locations && rule.blockLocations.locations.length > 0) {
              const locationType = rule.blockLocations.type || 'blacklist';
              const locationStrings = rule.blockLocations.locations.map((loc: any) => {
                let countryCode = 'ALL';
                let stateCode = 'ALL';
                let cityCode = 'ALL';

                if (loc.country && loc.country !== 'all') {
                  countryCode = loc.country.iso2 || loc.country;
                }
                if (loc.state && loc.state !== 'all') {
                  stateCode = loc.state.state_code || loc.state;
                }
                if (loc.city && loc.city !== 'all') {
                  cityCode = loc.city.name || loc.city;
                }

                return `${locationType}-${countryCode}-${stateCode}-${cityCode}`;
              });
              blockLocation = locationStrings.join(',');
            }

            let ipThrottle = '';
            if (rule.ipThrottle && rule.ipThrottle.ipThrottling) {
              ipThrottle = `${rule.ipThrottle.window}:${rule.ipThrottle.maxRequests}:${rule.ipThrottle.blacklistFor}`;
            }

            const row = [
              campaign.name || '',
              rule.blockAnon ? 'true' : 'false',
              rule.blockDataCenter ? 'true' : 'false',
              rule.blockByDetector ? 'true' : 'false',
              rule.blockBadIps && rule.blockBadIps.length > 0 ? 'true' : 'false',
              'false',
              blockLocation,
              ipThrottle,
              rule.globalFallback || '',
              rule.passback ? 'true' : 'false',
              rule.isPassbackUrl ? (rule.globalFallback || '') : '',
            ];
            rows.push(row.map((cell) => `"${cell}"`).join(','));
          });
        }
      });

      if (rows.length === 1) {
        showMessage({ message: 'No campaigns to export.', severity: 'info' });
        return;
      }

      const now = new Date();
      const dateStr = now.getFullYear() + '-' +
                    String(now.getMonth() + 1).padStart(2, '0') + '-' +
                    String(now.getDate()).padStart(2, '0');
      const timeStr = String(now.getHours()).padStart(2, '0') +
                    String(now.getMinutes()).padStart(2, '0');
      const filename = `IronDome_BlockSettings_${dateStr}_${timeStr}.csv`;

      const csvString = rows.join('\n');
      const blob = new Blob([csvString], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.setAttribute('href', url);
      a.setAttribute('download', filename);
      a.click();
      a.remove();

      showMessage({
        message: `Exported ${campaigns.length} campaign block settings to ${filename}`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error exporting Iron Dome settings:', error);
      showMessage({ message: 'Error exporting Iron Dome settings.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper>
      <Box mt={4} p={1}>
        <Box mb={2} display="flex" justifyContent="space-between">
          <Box display={'flex'} gap={1} alignItems={'center'}>
            <Button startIcon={<Download />} onClick={handleDownloadTemplate}>
              Download Template
            </Button>
            <Button startIcon={<UploadFile />} onClick={handleUploadDialogOpen}>
              Upload Offers
            </Button>
            <IronDomeImporter
              type="campaign"
              importFunction={ironDomeBulkImport().call}
              setLoading={setLoading}
              fetchData={fetchData}
            />
            <Button startIcon={<Download />} onClick={handleIronDomeExport}>
              Iron Dome Export
            </Button>
            <Button startIcon={<Download />} onClick={handleExportOffers}>
              Export Offers
            </Button>
          </Box>
          <Box display={'flex'} gap={1}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleDialogOpen()}
            >
              Add Campaign
            </Button>
          </Box>
        </Box>
        <Box
          className="ag-theme-alpine"
          style={{ height: '80vh', width: '100%' }}
        >
          <AgGridReact
            rowData={data}
            columnDefs={columns}
            defaultColDef={{
              sortable: true,
              filter: true,
              floatingFilter: true,
              flex: 1,
            }}
            pagination
            paginationPageSize={paginationModel.pageSize}
            onPaginationChanged={onPaginationChanged}
            onGridReady={(params) => {
              setLoading(false);
            }}
            enableCellTextSelection={true}
            loadingOverlayComponentParams={{ loading }}
          />
        </Box>
        <CampaignDialog
          open={isDialogOpen}
          onClose={handleDialogClose}
          onSave={handleSaveCampaign}
          initialCampaign={selectedCampaign}
        />
        <UploadDialog
          open={isUploadDialogOpen}
          onClose={handleUploadDialogClose}
          onSubmit={handleFileSubmit}
          error={uploadErrors || ''}
          setLoading={setLoading}
        />
      </Box>
    </Paper>
  );
}

export default CampaignTable;
